import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { HubConnection } from '@microsoft/signalr';
import { Doctor, Patient, TeleVisitQueueItem, StoreListItem, TeleStaffChatMessage } from '../../types/api';
import { apiService } from '../../services/api';
import {
  buildTeleconsultStaffChatHubConnection,
  getTeleconsultStaffChatHubUrl,
  canonicalChatVisitUlid,
} from '../../realtime/teleconsultStaffChatHub';
import { renderChatMessageLinks } from '../../utils/renderChatMessageLinks';
import { CaseChatUnreadBadge } from '../../components/queue/CaseChatUnreadBadge';
import { QueueTriageSummary } from '../../components/queue/QueueTriageSummary';
import { NewPatientBadge } from '../../components/queue/NewPatientBadge';
import { CorporateCompanyBadge } from '../../components/queue/CorporateCompanyBadge';
import { ConsultPartnerCodeBadge } from '../../components/queue/ConsultPartnerCodeBadge';
import { ChasBadge, HssAppliedChip } from '../../components/hss/ChasBadge';
import { MeetPresenceBadge } from '../../components/queue/MeetPresenceBadge';
import { PatientIdDocumentViewerButton } from '../../components/dispense/PatientIdDocumentVerifier';
import { parseTriageSummary } from '../../utils/parseTriageSummary';
import { getApiErrorMessage } from '../../utils/apiError';
import { AspenHealthLogo } from '../../components/AspenHealthLogo';
import { isConsultFinished, isCheckedOutVisit, isCancelledVisit } from '../../utils/visitStage';
import {
  StaffNormalSupportProvider,
  StaffNormalSupportQueue,
  StaffNormalSupportDock,
  useStaffNormalSupportTabMetrics,
  useStaffNormalSupport,
} from './StaffNormalSupportPanel';
import { StaffMessengerHubProvider, StaffMessengerThreadSwitcher, type MessengerThread } from './StaffMessengerHub';
import { CopyMeetLinkButton } from './CopyMeetLinkButton';
import { StaffManagementPanel, useManagementAccess } from '../staff-management';
import {
  defaultTeleconsultStoreFilter,
  filterStoresForTeleconsultQueue,
  formatTeleconsultQueueStoreLabel,
  isTeleconsultQueueStoreLocked,
  resolveTeleconsultQueueStoreId,
} from '../../config/teleconsultQueueStore';
import { patientPortalOriginFromEnv } from '../../config/teleconsultPatientPortal';
import { formatTimeSG, getTodayISO } from '../../utils/date';
import { countsAsStaffUnreadPatientMessage, parseChatMessages, staffChatSenderFields, sameMessageList, appendChatMessage, chatHeadInitials } from './staffChatMessages';

function QueueTabChatIcon({ unread }: { unread: number }) {
  if (unread <= 0) return null;
  return (
    <span
      className="staff-queue-tabs__chat-unread"
      aria-label={`${unread} unread patient message${unread === 1 ? '' : 's'}`}
    >
      <svg className="staff-queue-tabs__chat-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
        <path
          fillRule="evenodd"
          d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
          clipRule="evenodd"
        />
      </svg>
      <span className="staff-queue-tabs__unread-badge">{unread > 99 ? '99+' : unread}</span>
    </span>
  );
}

interface StaffTeleconsultQueuePageProps {
  staff: Doctor | null;
  onSelectPatient: (patient: Patient) => void;
  onLogout: () => void;
}

/** CheckLine VisitType 991 — tele med pickup/delivery on a physical clinic. */
const VISIT_TYPE_TELE_MED_FULFILLMENT = 991;

function isMedFulfillmentVisit(p: Patient): boolean {
  return p.visitType === VISIT_TYPE_TELE_MED_FULFILLMENT;
}

function fulfillmentModeFromRemarks(remarks?: string | null): 'Pickup' | 'Delivery' | null {
  const t = (remarks ?? '').trimStart();
  if (t.startsWith('[Delivery]')) return 'Delivery';
  if (t.startsWith('[Pickup]')) return 'Pickup';
  return null;
}

function collectAtFromRemarks(remarks?: string | null): string | null {
  const m = (remarks ?? '').match(/Collect (\d{1,2} \w{3} \d{4} \d{2}:\d{2})/i);
  return m?.[1] ?? null;
}

function mapQueueToPatients(items: TeleVisitQueueItem[], idOffset: number): Patient[] {
  return items.map((item, index) => ({
    id: idOffset + index + 1,
    visitULID: item.visitULID.toString(),
    queueNo: item.queueNo,
    name: item.patientName,
    nric: item.nric,
    age: item.age,
    gender: item.gender === 'M' || item.gender === 'F' ? item.gender : ('M' as const),
    store: item.storeName,
    storeId: item.storeID,
    storeCode: item.storeCode,
    visitTime: item.visitTime,
    temp: item.temperature ?? 0,
    sbp: item.sbp ?? 0,
    dbp: item.dbp ?? 0,
    pulse: item.pulse ?? 0,
    spo2: item.spO2 ?? 0,
    remarks: item.remarks || '',
    allergies: item.allergies || [],
    chronicConditions: item.chronicConditions || [],
    triageSummary: item.triageSummary ?? undefined,
    visitStage: item.visitStage,
    visitType: item.visitType,
    walkIn: item.walkIn,
    teleMed: item.teleMed,
    newPt: item.newPt,
    corporateCompanyName: item.corporateCompanyName,
    consultPartnerCode: item.consultPartnerCode ?? undefined,
    chas: item.chas ?? null,
    hssApplied: item.hssApplied ?? false,
    consultFinalized: item.consultFinalized ?? false,
    autoCheckoutEnabled: item.autoCheckoutEnabled ?? false,
    needsFulfillmentChoice: item.needsFulfillmentChoice ?? false,
    needsExtraPayment: item.needsExtraPayment ?? false,
    empId: item.empId ?? undefined,
    empCode: item.empCode ?? undefined,
    chatInchargeEmpId: item.chatInchargeEmpId ?? undefined,
    chatInchargeEmpCode: item.chatInchargeEmpCode ?? undefined,
    unreadPatientChatCount: item.unreadPatientChatCount ?? 0,
    patientIdVettingPending: item.patientIdVettingPending ?? false,
    googleMeetUrl: item.googleMeetUrl ?? null,
    corporateZoomSessionBlocked: item.corporateZoomSessionBlocked ?? false,
    patientInRoom: item.patientInRoom ?? null,
  }));
}

function isUnpickedVisit(p: Patient): boolean {
  return p.empId == null || p.empId === 0;
}

function chatInchargeId(p: Patient): number | null {
  const v = p.chatInchargeEmpId;
  if (v == null || v === 0) return null;
  return v;
}

function isMyChatPatient(p: Patient, staffId?: number): boolean {
  if (!staffId) return false;
  return chatInchargeId(p) === staffId;
}

function isChatTakenByOther(p: Patient, staffId?: number): boolean {
  const id = chatInchargeId(p);
  if (id == null) return false;
  return id !== staffId;
}

function splitMineAndOthers(list: Patient[], staffId?: number): { mine: Patient[]; others: Patient[] } {
  if (!staffId) return { mine: [], others: list };
  const mine = list.filter((p) => isMyChatPatient(p, staffId));
  const others = list.filter((p) => !isMyChatPatient(p, staffId));
  return { mine, others };
}

function latestPatientMessageTs(messages: TeleStaffChatMessage[]): number {
  return Math.max(
    0,
    ...messages.filter((m) => m.sender === 'patient' || m.sender === 'partner').map((m) => m.ts),
  );
}

function markPatientMessagesSeen(lastSeen: number, messages: TeleStaffChatMessage[]): number {
  return Math.max(lastSeen, latestPatientMessageTs(messages));
}

const POLL_MS_FOREGROUND = 2800;
const POLL_MS_BACKGROUND = 12000;
/** When SignalR is connected, poll only as a slow safety net (tab visible). */
const POLL_MS_SIGNALR_FALLBACK = 20_000;
const MAX_OPEN_CHATS = 8;
/** Queue list refresh — chat incharge changes should show quickly across staff stations. */
const QUEUE_POLL_MS = 12_000;
/** Patient joined Meet toast — stays until dismissed or this timeout (10 minutes). */
const PATIENT_READY_TOAST_AUTO_DISMISS_MS = 10 * 60 * 1000;

/** Parse staff-entered SGD amount (major units). Returns null if invalid. */
function parseSgdStaffPaymentAmount(raw: string): number | null {
  const t = raw.trim().replace(/,/g, '');
  if (!t) return null;
  const n = Number.parseFloat(t);
  if (!Number.isFinite(n) || n < 0.01 || n > 50_000) return null;
  return n;
}

type QueueTab = 'pending' | 'queue' | 'support' | 'completed' | 'management';

type TeleCardBucket = 'waiting' | 'withDoctor' | 'completed';

/** Stage 60 CheckOut (Julius Aspen Tele / normal checkout) — includes VisitType 991 after bill. */
function isCompletedVisit(p: Patient): boolean {
  const stage = p.visitStage ?? 0;
  if (stage === 99) return false;
  return stage === 60 || isCheckedOutVisit(stage, p.remarks);
}

/** Staff pending: ID vetting, Julius checkout after consult, or open VisitType 991 med fulfillment. */
function needsStaffPendingAction(p: Patient): boolean {
  if (isCompletedVisit(p)) return false;
  if (isMedFulfillmentVisit(p)) return true;
  if (p.patientIdVettingPending === true) return true;
  return isConsultFinished(p.visitStage);
}

/** Waiting for doctor to consult (before SeenDr / stage 30), excluding staff-pending items. */
function isDoctorQueueVisit(p: Patient): boolean {
  if (isCompletedVisit(p)) return false;
  if (isMedFulfillmentVisit(p)) return false;
  if (needsStaffPendingAction(p)) return false;
  return !isConsultFinished(p.visitStage);
}

function cardBucketForPatient(p: Patient): TeleCardBucket {
  if (isCompletedVisit(p)) return 'completed';
  if (isMedFulfillmentVisit(p)) return 'waiting';
  if (isConsultFinished(p.visitStage)) return 'withDoctor';
  return isUnpickedVisit(p) ? 'waiting' : 'withDoctor';
}

type OpenChat = {
  visitUlid: string;
  patient: Patient;
  messages: TeleStaffChatMessage[];
  draft: string;
  expanded: boolean;
  chatError: string | null;
  sending: boolean;
  isClosed: boolean;
  /** When collapsed: patient msgs after this ts count as “unread” */
  lastSeenPatientTs: number;
};

/** Scrollable message list; keeps view pinned to latest when messages change (patient/staff/poll/SignalR). */
function StaffChatMessagesPanel({
  visitUlid,
  messages,
  onPatientIdSaved,
}: {
  visitUlid: string;
  messages: TeleStaffChatMessage[];
  onPatientIdSaved?: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [imageError, setImageError] = useState<Record<string, string>>({});
  const [savingImageUlid, setSavingImageUlid] = useState<string | null>(null);

  const patientImageUlids = useMemo(
    () =>
      messages
        .filter((m) => m.imageUlid && m.sender === 'patient')
        .map((m) => m.imageUlid as string),
    [messages],
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const run = () => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
    };
    run();
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
    return () => cancelAnimationFrame(id);
  }, [messages]);

  const attemptedImagesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    for (const imageUlid of patientImageUlids) {
      if (attemptedImagesRef.current.has(imageUlid)) continue;
      attemptedImagesRef.current.add(imageUlid);
      void apiService
        .getTeleconsultStaffChatImage(visitUlid, imageUlid)
        .then((blob) => {
          if (cancelled) return;
          setImageUrls((prev) =>
            prev[imageUlid] ? prev : { ...prev, [imageUlid]: URL.createObjectURL(blob) },
          );
        })
        .catch(() => {
          if (cancelled) return;
          setImageError((prev) => ({ ...prev, [imageUlid]: 'Could not load image' }));
        });
    }
    return () => {
      cancelled = true;
    };
  }, [patientImageUlids, visitUlid]);

  useEffect(() => {
    return () => {
      Object.values(imageUrls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [imageUrls]);

  const handleSaveAsPatientId = async (imageUlid: string) => {
    setSavingImageUlid(imageUlid);
    try {
      await apiService.saveTeleconsultStaffChatImageAsPatientId(visitUlid, imageUlid);
      onPatientIdSaved?.();
    } catch (e) {
      setImageError((prev) => ({
        ...prev,
        [imageUlid]: getApiErrorMessage(e, 'Could not save as Patient ID'),
      }));
    } finally {
      setSavingImageUlid(null);
    }
  };

  return (
    <div className="staff-messenger-window__messages" ref={scrollRef}>
      {messages.length === 0 ? (
        <p className="staff-messenger-window__empty">No messages yet.</p>
      ) : (
        messages.map((m, idx) => (
          <div
            key={`${m.ts}-${idx}`}
            className={`staff-msg-bubble ${
              m.sender === 'system'
                ? 'staff-msg-bubble--system'
                : m.sender === 'staff'
                  ? 'staff-msg-bubble--staff'
                  : 'staff-msg-bubble--patient'
            }`}
          >
            <span className="staff-msg-bubble__who">
              {m.sender === 'staff'
                ? 'You'
                : m.sender === 'system'
                  ? 'System'
                  : m.sender === 'partner'
                    ? 'Partner'
                    : 'Patient'}
            </span>
            {m.text.trim() &&
            !(m.imageUlid && (m.text === 'Photo' || m.text === 'PDF')) ? (
              <span className="staff-msg-bubble__body">{renderChatMessageLinks(m.text)}</span>
            ) : null}
            {m.imageUlid ? (
              <div className="staff-msg-bubble__attachment">
                {!imageUrls[m.imageUlid] && !imageError[m.imageUlid] ? (
                  <span className="staff-msg-bubble__attachment-status">Loading image…</span>
                ) : imageUrls[m.imageUlid] ? (
                  (m.imageMimeType ?? '').includes('pdf') ? (
                    <a href={imageUrls[m.imageUlid]} target="_blank" rel="noopener noreferrer">
                      View PDF attachment
                    </a>
                  ) : (
                    <img src={imageUrls[m.imageUlid]} alt="Patient attachment" className="staff-msg-bubble__image" />
                  )
                ) : (
                  <span className="staff-msg-bubble__attachment-status">
                    {imageError[m.imageUlid] ?? 'Image unavailable'}
                  </span>
                )}
                {m.sender === 'patient' && m.imageUlid ? (
                  <div className="staff-msg-bubble__attachment-actions">
                    {imageUrls[m.imageUlid] ? (
                      <a
                        href={imageUrls[m.imageUlid]}
                        download={m.imageFileName ?? 'patient-id.jpg'}
                        className="btn btn-secondary btn-sm"
                      >
                        Download
                      </a>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={savingImageUlid === m.imageUlid}
                      onClick={() => void handleSaveAsPatientId(m.imageUlid!)}
                    >
                      {savingImageUlid === m.imageUlid ? 'Saving…' : 'Save as Patient ID'}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
            <span className="staff-msg-bubble__time">
              {formatTimeSG(m.ts)}
            </span>
          </div>
        ))
      )}
    </div>
  );
}

export default function StaffTeleconsultQueuePage(props: StaffTeleconsultQueuePageProps) {
  return (
    <StaffNormalSupportProvider staff={props.staff}>
      <StaffTeleconsultQueuePageContent {...props} />
    </StaffNormalSupportProvider>
  );
}

function StaffTeleconsultQueuePageContent({ staff, onSelectPatient, onLogout }: StaffTeleconsultQueuePageProps) {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [stores, setStores] = useState<StoreListItem[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(getTodayISO());
  const [storeFilter, setStoreFilter] = useState<string>(defaultTeleconsultStoreFilter());
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [queueTab, setQueueTab] = useState<QueueTab>('queue');
  const showManagementTab = useManagementAccess(staff?.code, staff?.roleId, staff?.username);
  const { activeCount: supportActiveCount, unhandledCount: supportUnhandledCount } =
    useStaffNormalSupportTabMetrics();
  const supportChat = useStaffNormalSupport();

  useEffect(() => {
    if (!showManagementTab && queueTab === 'management') {
      setQueueTab('queue');
    }
  }, [showManagementTab, queueTab]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia('(max-width: 768px)').matches) return;
    if (!supportChat.openChats.some((c) => c.expanded)) return;
    setOpenChats((prev) => {
      if (!prev.some((c) => c.expanded)) return prev;
      return prev.map((c) => ({ ...c, expanded: false }));
    });
  }, [supportChat.openChats]);
  const [claimingVisitUlid, setClaimingVisitUlid] = useState<string | null>(null);
  const [releasingChatVisitUlid, setReleasingChatVisitUlid] = useState<string | null>(null);
  const [closingChatVisitUlid, setClosingChatVisitUlid] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unclaimingVisitUlid, setUnclaimingVisitUlid] = useState<string | null>(null);
  const [meetUrlOverrides, setMeetUrlOverrides] = useState<Record<string, string>>({});

  const [openChats, setOpenChats] = useState<OpenChat[]>([]);
  const openChatsRef = useRef<OpenChat[]>([]);
  openChatsRef.current = openChats;
  const [staffChatHubConnected, setStaffChatHubConnected] = useState(false);
  const staffChatHubRef = useRef<HubConnection | null>(null);
  const visitUlidsForHubRef = useRef<string[]>([]);
  const [stripePaymentVisitUlid, setStripePaymentVisitUlid] = useState<string | null>(null);
  const [stripePaymentAmount, setStripePaymentAmount] = useState('');
  const [stripePaymentError, setStripePaymentError] = useState<string | null>(null);
  const [patientPortalOrigin, setPatientPortalOrigin] = useState(patientPortalOriginFromEnv);
  const patientReadyToastTimersRef = useRef<Map<string, number>>(new Map());
  const [patientReadyToasts, setPatientReadyToasts] = useState<
    Array<{ key: string; visitUlid: string; patientName: string }>
  >([]);

  const dismissPatientReadyToast = useCallback((key: string) => {
    const timer = patientReadyToastTimersRef.current.get(key);
    if (timer != null) {
      window.clearTimeout(timer);
      patientReadyToastTimersRef.current.delete(key);
    }
    setPatientReadyToasts((prev) => prev.filter((t) => t.key !== key));
  }, []);

  const showPatientReadyToast = useCallback(
    (visitUlid: string, patientName: string) => {
      const ulid = canonicalChatVisitUlid(visitUlid);
      if (!ulid) return;
      const key = `${ulid}-${Date.now()}`;
      const name = patientName.trim() || 'Patient';
      setPatientReadyToasts((prev) => [...prev, { key, visitUlid: ulid, patientName: name }]);
      const timer = window.setTimeout(
        () => dismissPatientReadyToast(key),
        PATIENT_READY_TOAST_AUTO_DISMISS_MS,
      );
      patientReadyToastTimersRef.current.set(key, timer);
    },
    [dismissPatientReadyToast],
  );

  useEffect(
    () => () => {
      patientReadyToastTimersRef.current.forEach((timer) => {
        window.clearTimeout(timer);
      });
      patientReadyToastTimersRef.current.clear();
    },
    [],
  );

  const openVisitUlidsKey = useMemo(
    () => Array.from(new Set(openChats.map((c) => c.visitUlid))).sort().join('|'),
    [openChats],
  );

  useEffect(() => {
    void (async () => {
      try {
        const storeList = filterStoresForTeleconsultQueue(await apiService.getStores());
        setStores(storeList);
      } catch (err) {
        console.error('StaffTeleconsultQueuePage: Error loading stores:', err);
      }
    })();
  }, []);

  useEffect(() => {
    void apiService.getTeleconsultPortalConfig().then((config) => {
      const origin = config.patientPortalOrigin?.trim().replace(/\/$/, '');
      if (origin) setPatientPortalOrigin(origin);
    }).catch(() => {
      /* keep env/default fallback */
    });
  }, []);

  const loadQueue = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const storeId = resolveTeleconsultQueueStoreId(storeFilter);
      const items = await apiService.getTeleVisitQueue(selectedDate, storeId, 'all');
      setPatients(mapQueueToPatients(items, 0));
      if (silent) setError(null);
    } catch (err: unknown) {
      if (!silent) {
        setError(getApiErrorMessage(err, 'Could not load the queue. Please try again.'));
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedDate, storeFilter]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  /** Keep open chat headers in sync when the queue refreshes (e.g. incharge changes). */
  useEffect(() => {
    setOpenChats((prev) =>
      prev.map((c) => {
        const np = patients.find((p) => p.visitULID === c.visitUlid);
        return np ? { ...c, patient: np } : c;
      }),
    );
  }, [patients]);

  /** Keep queue list fresh when doctors pick visits (no loading spinner). */
  useEffect(() => {
    const id = window.setInterval(() => void loadQueue({ silent: true }), QUEUE_POLL_MS);
    return () => window.clearInterval(id);
  }, [loadQueue]);

  /** Poll open chats; throttles in background tab. Skips chats mid-send and avoids re-renders when JSON unchanged. */
  useEffect(() => {
    let intervalId: number | undefined;
    let inFlight = false;

    const poll = async () => {
      if (inFlight) return;
      const list = openChatsRef.current;
      if (list.length === 0) return;
      inFlight = true;
      try {
        const pairs = await Promise.all(
          list.map(async (c) => {
            try {
              const res = await apiService.getTeleconsultStaffChat(c.visitUlid);
              return { visitUlid: c.visitUlid, messages: parseChatMessages(res.chatConversation) };
            } catch {
              return { visitUlid: c.visitUlid, messages: null as TeleStaffChatMessage[] | null };
            }
          }),
        );
        setOpenChats((prev) =>
          prev.map((c) => {
            const p = pairs.find((x) => x.visitUlid === c.visitUlid);
            if (!p || p.messages === null) return c;
            if (c.sending) return c;
            if (sameMessageList(c.messages, p.messages)) return c;
            return {
              ...c,
              messages: p.messages,
              lastSeenPatientTs: c.expanded
                ? markPatientMessagesSeen(c.lastSeenPatientTs, p.messages)
                : c.lastSeenPatientTs,
            };
          }),
        );
      } finally {
        inFlight = false;
      }
    };

    const armInterval = () => {
      if (intervalId != null) window.clearInterval(intervalId);
      const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
      const ms = hidden
        ? POLL_MS_BACKGROUND
        : staffChatHubConnected
          ? POLL_MS_SIGNALR_FALLBACK
          : POLL_MS_FOREGROUND;
      intervalId = window.setInterval(() => void poll(), ms);
    };

    const onVisibility = () => {
      armInterval();
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        void poll();
      }
    };

    void poll();
    armInterval();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (intervalId != null) window.clearInterval(intervalId);
    };
  }, [staffChatHubConnected]);

  /** Realtime chat updates for open visits + staff queue alerts; polling remains as fallback. */
  useEffect(() => {
    const visitIds = openVisitUlidsKey ? openVisitUlidsKey.split('|').filter(Boolean) : [];
    visitUlidsForHubRef.current = visitIds;

    const hubUrl = getTeleconsultStaffChatHubUrl();
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('accessToken') : null;
    const conn = buildTeleconsultStaffChatHubConnection({
      hubUrl,
      accessToken: token,
      onStaffChatUpdated: (payload) => {
        const ulid = canonicalChatVisitUlid(payload.visitUlid);
        if (!ulid || typeof payload.chatConversation !== 'string') return;
        const messages = parseChatMessages(payload.chatConversation);
        setOpenChats((prev) =>
          prev.map((c) => {
            if (canonicalChatVisitUlid(c.visitUlid) !== ulid) return c;
            if (c.sending) return c;
            if (sameMessageList(c.messages, messages)) return c;
            return {
              ...c,
              messages,
              chatError: null,
              lastSeenPatientTs: c.expanded
                ? markPatientMessagesSeen(c.lastSeenPatientTs, messages)
                : c.lastSeenPatientTs,
            };
          }),
        );
      },
      onPatientReadyForMeet: (payload) => {
        if (typeof payload.visitUlid !== 'string' || typeof payload.patientName !== 'string') return;
        showPatientReadyToast(payload.visitUlid, payload.patientName);
      },
      onTeleVisitQueueUpdated: () => {
        void loadQueue({ silent: true });
      },
      onMeetPresenceUpdated: (payload) => {
        const ulid = typeof payload.visitUlid === 'string' ? payload.visitUlid.trim().toLowerCase() : '';
        if (!ulid || typeof payload.patientInRoom !== 'boolean') return;
        setPatients((prev) =>
          prev.map((p) =>
            p.visitULID.toLowerCase() === ulid ? { ...p, patientInRoom: payload.patientInRoom } : p,
          ),
        );
      },
    });

    const joinAll = async () => {
      try {
        await conn.invoke('JoinStaffQueue');
      } catch {
        /* hub may still be negotiating */
      }
      for (const id of visitUlidsForHubRef.current) {
        try {
          await conn.invoke('JoinVisit', id);
        } catch {
          /* hub may still be negotiating */
        }
      }
    };

    conn.onreconnected(() => {
      void joinAll();
    });

    staffChatHubRef.current = conn;
    let cancelled = false;

    void (async () => {
      try {
        await conn.start();
        if (cancelled) return;
        await joinAll();
        if (cancelled) return;
        setStaffChatHubConnected(true);
      } catch (e) {
        console.warn('[SignalR teleconsult staff chat]', e);
        if (!cancelled) setStaffChatHubConnected(false);
      }
    })();

    return () => {
      cancelled = true;
      setStaffChatHubConnected(false);
      void conn.stop();
      if (staffChatHubRef.current === conn) {
        staffChatHubRef.current = null;
      }
    };
  }, [openVisitUlidsKey, showPatientReadyToast, loadQueue]);

  const filterList = (list: Patient[]) => {
    if (!searchTerm.trim()) return list;
    const q = searchTerm.toLowerCase();
    return list.filter((p) => {
      const triageHaystack = [p.triageSummary ?? '', ...parseTriageSummary(p.triageSummary)].join(' ').toLowerCase();
      return p.name.toLowerCase().includes(q) || p.nric.toLowerCase().includes(q) || triageHaystack.includes(q);
    });
  };

  const completedPatients = filterList(patients.filter(isCompletedVisit));
  const completedMedFulfillmentPatients = completedPatients.filter(isMedFulfillmentVisit);
  const completedTelePatients = completedPatients.filter((p) => !isMedFulfillmentVisit(p));
  const staffPendingPatients = filterList(patients.filter(needsStaffPendingAction));
  const doctorQueuePatients = filterList(patients.filter(isDoctorQueueVisit));
  const medFulfillmentPendingPatients = staffPendingPatients.filter(isMedFulfillmentVisit);
  const idVettingPatients = staffPendingPatients.filter(
    (p) => !isMedFulfillmentVisit(p) && p.patientIdVettingPending === true,
  );
  const checkoutPendingPatients = staffPendingPatients.filter(
    (p) =>
      !isMedFulfillmentVisit(p) &&
      !p.patientIdVettingPending &&
      isConsultFinished(p.visitStage),
  );
  const queueWaitingPatients = doctorQueuePatients.filter(isUnpickedVisit);
  const queueWithDoctorPatients = doctorQueuePatients.filter((p) => !isUnpickedVisit(p));
  const staffId = staff?.id;

  const staffPendingMine = staffId
    ? staffPendingPatients.filter((p) => !isMedFulfillmentVisit(p) && isMyChatPatient(p, staffId))
    : [];
  const idVettingOthers = staffId
    ? idVettingPatients.filter((p) => !isMyChatPatient(p, staffId))
    : idVettingPatients;
  const checkoutPendingOthers = staffId
    ? checkoutPendingPatients.filter((p) => !isMyChatPatient(p, staffId))
    : checkoutPendingPatients;

  const queueMine = staffId ? doctorQueuePatients.filter((p) => isMyChatPatient(p, staffId)) : [];
  const queueWaitingOthers = staffId
    ? queueWaitingPatients.filter((p) => !isMyChatPatient(p, staffId))
    : queueWaitingPatients;
  const queueWithDoctorOthers = staffId
    ? queueWithDoctorPatients.filter((p) => !isMyChatPatient(p, staffId))
    : queueWithDoctorPatients;

  const refreshOneChat = async (visitUlid: string) => {
    try {
      const res = await apiService.getTeleconsultStaffChat(visitUlid);
      const messages = parseChatMessages(res.chatConversation);
      setOpenChats((prev) =>
        prev.map((c) => {
          if (c.visitUlid !== visitUlid) return c;
          const isClosed = res.isClosed === true;
          if (sameMessageList(c.messages, messages) && c.isClosed === isClosed) return c;
          return {
            ...c,
            messages,
            chatError: null,
            isClosed,
            lastSeenPatientTs: c.expanded
              ? markPatientMessagesSeen(c.lastSeenPatientTs, messages)
              : c.lastSeenPatientTs,
          };
        }),
      );
    } catch (err: unknown) {
      const msg = getApiErrorMessage(err, 'Could not load chat. Please try again.');
      setOpenChats((prev) => prev.map((c) => (c.visitUlid === visitUlid ? { ...c, chatError: msg } : c)));
    }
  };

  const openOrFocusChat = (p: Patient) => {
    if (!isMyChatPatient(p, staffId)) {
      return;
    }
    let isNew = false;
    let existed = false;
    setOpenChats((prev) => {
      const existing = prev.find((c) => c.visitUlid === p.visitULID);
      if (existing) {
        existed = true;
        return prev.map((c) =>
          c.visitUlid === p.visitULID
            ? {
                ...c,
                expanded: true,
                patient: p,
                lastSeenPatientTs: markPatientMessagesSeen(c.lastSeenPatientTs, c.messages),
              }
            : c,
        );
      }
      if (prev.length >= MAX_OPEN_CHATS) {
        return prev;
      }
      isNew = true;
      return [
        ...prev.map((c) => ({ ...c, expanded: false })),
        {
          visitUlid: p.visitULID,
          patient: p,
          messages: [],
          draft: '',
          expanded: true,
          chatError: null,
          sending: false,
          isClosed: false,
          lastSeenPatientTs: 0,
        },
      ];
    });
    setSidebarOpen(false);
    if (existed) void refreshOneChat(p.visitULID);
    if (isNew) window.setTimeout(() => void refreshOneChat(p.visitULID), 0);
  };

  const closeChat = useCallback((visitUlid: string) => {
    setStripePaymentVisitUlid((v) => (v === visitUlid ? null : v));
    setOpenChats((prev) => prev.filter((c) => c.visitUlid !== visitUlid));
  }, []);

  useEffect(() => {
    if (stripePaymentVisitUlid == null) {
      setStripePaymentAmount('');
      setStripePaymentError(null);
    }
  }, [stripePaymentVisitUlid]);

  const staffUnclaimVisit = useCallback(
    async (p: Patient) => {
      const ulid = p.visitULID;
      if (
        !window.confirm(
          `Return ${p.name} (${p.queueNo}) to the doctor queue? The visit will no longer be assigned to the current doctor.`,
        )
      ) {
        return;
      }
      setUnclaimingVisitUlid(ulid);
      setError(null);
      try {
        await apiService.staffUnclaimTeleVisit(ulid);
        closeChat(ulid);
        await loadQueue({ silent: true });
      } catch (err: unknown) {
        let msg = 'Could not unclaim visit';
        msg = getApiErrorMessage(err, msg);
        setError(msg);
      } finally {
        setUnclaimingVisitUlid(null);
      }
    },
    [loadQueue, closeChat],
  );

  const staffPickChatForVisit = useCallback(
    async (p: Patient) => {
      if (isChatTakenByOther(p, staffId) || isMyChatPatient(p, staffId)) return;
      setClaimingVisitUlid(p.visitULID);
      setError(null);
      try {
        await apiService.claimTeleconsultChatForVisit(p.visitULID);
        await loadQueue({ silent: true });
        if (staffId) {
          openOrFocusChat({ ...p, chatInchargeEmpId: staffId });
        }
      } catch (err: unknown) {
        let msg = 'Could not claim chat for this visit';
        msg = getApiErrorMessage(err, msg);
        setError(msg);
      } finally {
        setClaimingVisitUlid(null);
      }
    },
    [loadQueue, staffId],
  );

  const staffReleaseChatClaim = useCallback(
    async (visitUlid: string) => {
      setReleasingChatVisitUlid(visitUlid);
      setError(null);
      try {
        await apiService.releaseTeleconsultChatForVisit(visitUlid);
        closeChat(visitUlid);
        await loadQueue({ silent: true });
      } catch (err: unknown) {
        let msg = 'Could not release chat claim';
        msg = getApiErrorMessage(err, msg);
        setError(msg);
      } finally {
        setReleasingChatVisitUlid(null);
      }
    },
    [loadQueue, closeChat],
  );

  const staffCloseChat = useCallback(
    async (visitUlid: string) => {
      if (!window.confirm('End this chat? The patient will see a closing message and cannot send more until you pick the visit again.')) {
        return;
      }
      setClosingChatVisitUlid(visitUlid);
      setError(null);
      try {
        await apiService.closeTeleconsultChatForVisit(visitUlid);
        closeChat(visitUlid);
        await loadQueue({ silent: true });
      } catch (err: unknown) {
        let msg = 'Could not end chat';
        msg = getApiErrorMessage(err, msg);
        setError(msg);
      } finally {
        setClosingChatVisitUlid(null);
      }
    },
    [loadQueue, closeChat],
  );

  const setExpanded = (visitUlid: string, expanded: boolean) => {
    if (!expanded) {
      setStripePaymentVisitUlid((v) => (v === visitUlid ? null : v));
    }
    const collapseSiblings =
      expanded && typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
    if (collapseSiblings) {
      supportChat.setOpenChats((prev) => prev.map((c) => ({ ...c, expanded: false })));
    }
    setOpenChats((prev) =>
      prev.map((c) => {
        if (c.visitUlid !== visitUlid) {
          return collapseSiblings ? { ...c, expanded: false } : c;
        }
        return {
          ...c,
          expanded,
          // Mark read when minimizing (or while open) so collapsed pill / tab badges clear.
          lastSeenPatientTs: markPatientMessagesSeen(c.lastSeenPatientTs, c.messages),
        };
      }),
    );
  };

  const setDraftFor = (visitUlid: string, draft: string) => {
    setOpenChats((prev) => prev.map((c) => (c.visitUlid === visitUlid ? { ...c, draft } : c)));
  };

  const sendFor = async (visitUlid: string) => {
    const chat = openChatsRef.current.find((c) => c.visitUlid === visitUlid);
    if (!chat || chat.sending || chat.isClosed || !chat.draft.trim()) return;
    const text = chat.draft.replace(/\r\n/g, '\n').trim();
    const msg: TeleStaffChatMessage = { sender: 'staff', text, ts: Date.now(), ...staffChatSenderFields(staff) };
    setOpenChats((prev) =>
      prev.map((c) =>
        c.visitUlid === visitUlid
          ? { ...c, draft: '', messages: appendChatMessage(c.messages, msg), sending: true, chatError: null }
          : c,
      ),
    );
    try {
      const fresh = await apiService.getTeleconsultStaffChat(visitUlid);
      const serverMsgs = parseChatMessages(fresh.chatConversation);
      const merged = appendChatMessage(serverMsgs, msg);
      await apiService.saveTeleconsultStaffChat(visitUlid, merged, staff?.id ?? null);
      await refreshOneChat(visitUlid);
    } catch (err: unknown) {
      const errMsg = getApiErrorMessage(err, 'Could not send message. Please try again.');
      setOpenChats((prev) =>
        prev.map((c) => (c.visitUlid === visitUlid ? { ...c, chatError: errMsg, sending: false } : c)),
      );
      await refreshOneChat(visitUlid);
    } finally {
      setOpenChats((prev) => prev.map((c) => (c.visitUlid === visitUlid ? { ...c, sending: false } : c)));
    }
  };

  const closeStripePaymentDialog = useCallback(() => {
    setStripePaymentVisitUlid(null);
    setStripePaymentAmount('');
    setStripePaymentError(null);
  }, []);

  const sendPaymentLinkInChat = async (visitUlid: string, amountMajor: number) => {
    const chat = openChatsRef.current.find((x) => x.visitUlid === visitUlid);
    if (!chat || chat.sending) return;
    setStripePaymentError(null);
    setOpenChats((prev) =>
      prev.map((c) => (c.visitUlid === visitUlid ? { ...c, sending: true, chatError: null } : c)),
    );
    try {
      const portalBase = patientPortalOrigin;
      const paymentPageUrl = `${portalBase}/teleconsult-checkin/payment?visitUlid=${encodeURIComponent(visitUlid)}&amountMajor=${encodeURIComponent(String(amountMajor))}&source=staff`;
      const formatted = new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(amountMajor);
      const text = `Please complete payment (${formatted}) on the patient portal first — you will be taken to secure Stripe checkout, then back to your waiting room.\n${paymentPageUrl}`;
      const msg: TeleStaffChatMessage = { sender: 'staff', text, ts: Date.now(), ...staffChatSenderFields(staff) };
      const freshChat = await apiService.getTeleconsultStaffChat(visitUlid);
      const serverMsgs = parseChatMessages(freshChat.chatConversation);
      const merged = appendChatMessage(serverMsgs, msg);
      setOpenChats((prev) =>
        prev.map((c) => (c.visitUlid === visitUlid ? { ...c, messages: merged } : c)),
      );
      await apiService.saveTeleconsultStaffChat(visitUlid, merged, staff?.id ?? null);
      closeStripePaymentDialog();
      await refreshOneChat(visitUlid);
    } catch (err: unknown) {
      setStripePaymentError(getApiErrorMessage(err, 'Could not send payment link. Please try again.'));
      await refreshOneChat(visitUlid);
    } finally {
      setOpenChats((prev) => prev.map((c) => (c.visitUlid === visitUlid ? { ...c, sending: false } : c)));
    }
  };

  const unreadPatientCount = (c: OpenChat): number => {
    if (c.expanded) return 0;
    return c.messages.filter(
      (m) => countsAsStaffUnreadPatientMessage(m) && m.ts > c.lastSeenPatientTs,
    ).length;
  };

  const messengerThreads: MessengerThread[] = useMemo(() => {
    const tele: MessengerThread[] = openChats.map((c) => ({
      id: `tele:${c.visitUlid}`,
      kind: 'tele',
      name: c.patient.name,
      subtitle: c.patient.queueNo || '',
      unread: unreadPatientCount(c),
      expanded: c.expanded,
    }));
    const supportThreads: MessengerThread[] = supportChat.openChats.map((c) => {
      let unread = 0;
      if (!c.expanded) {
        for (let i = c.messages.length - 1; i >= 0; i--) {
          const sender = c.messages[i].sender;
          if (sender === 'patient' || sender === 'partner') unread += 1;
          else if (sender === 'staff') break;
        }
      }
      return {
        id: `support:${c.chatUlid}`,
        kind: 'support',
        name: c.item.visitorName?.trim() || 'Visitor',
        subtitle: (c.item.displaySubtitle || c.item.visitorPhone || 'Live support').trim(),
        unread,
        expanded: c.expanded,
      };
    });
    return [...tele, ...supportThreads];
  }, [openChats, supportChat.openChats]);

  const switchMessengerThread = useCallback((id: string) => {
    if (id.startsWith('tele:')) {
      const visitUlid = id.slice('tele:'.length);
      supportChat.setOpenChats((prev) => prev.map((c) => ({ ...c, expanded: false })));
      setOpenChats((prev) =>
        prev.map((c) => {
          if (c.visitUlid !== visitUlid) return { ...c, expanded: false };
          return {
            ...c,
            expanded: true,
            lastSeenPatientTs: markPatientMessagesSeen(c.lastSeenPatientTs, c.messages),
          };
        }),
      );
      return;
    }
    if (id.startsWith('support:')) {
      const chatUlid = id.slice('support:'.length);
      setStripePaymentVisitUlid(null);
      setOpenChats((prev) => prev.map((c) => ({ ...c, expanded: false })));
      supportChat.setOpenChats((prev) =>
        prev.map((c) => ({ ...c, expanded: c.chatUlid === chatUlid })),
      );
    }
  }, [supportChat.setOpenChats]);

  const patientQueueUnread = (p: Patient): number => {
    const openChat = openChats.find((c) => c.visitUlid === p.visitULID);
    if (openChat != null) return unreadPatientCount(openChat);
    return p.unreadPatientChatCount ?? 0;
  };

  const staffPendingUnreadTotal = staffPendingPatients.reduce((sum, p) => sum + patientQueueUnread(p), 0);
  const doctorQueueUnreadTotal = doctorQueuePatients.reduce((sum, p) => sum + patientQueueUnread(p), 0);
  const completedUnreadTotal = completedPatients.reduce((sum, p) => sum + patientQueueUnread(p), 0);

  /**
   * CheckLine / med.Visit.Stage: Reg=0, CheckIn=10, Triage=20, SeenDr=30, GivenMed=40, Payment=50, CheckOut=60, Cancel=99.
   * Match dr portal queue cards: blue = registered / waiting (before SeenDr), red = in consult (30–59), green = checked out (60+), gray = cancelled.
   * VisitType 991 (open): purple until Stage 60.
   */
  const stageTint = (stage?: number | null, remarks?: string | null, visitType?: number | null) => {
    const s = stage ?? 0;
    if (s === 99 || isCancelledVisit(s, remarks)) {
      return { bg: '#f1f5f9', border: '#94a3b8' };
    }
    if (visitType === VISIT_TYPE_TELE_MED_FULFILLMENT && s < 60) {
      return { bg: '#f3e8ff', border: '#c084fc' };
    }
    if (s >= 60) {
      return { bg: '#f0fdf4', border: '#86efac' };
    }
    if (s >= 30) {
      return { bg: '#fff1f2', border: '#fca5a5' };
    }
    // 0, 10, 20 — same palette as QueuePage stage 10 (waiting / check-in / triage)
    return { bg: '#eff6ff', border: '#93c5fd' };
  };

  const isChatOpen = (visitUlid: string) => openChats.some((c) => c.visitUlid === visitUlid);

  const completedSplit = splitMineAndOthers(completedTelePatients, staffId);

  const renderCompletedView = () => (
    <div className="staff-queue-pending-layout">
      {renderDuoVertical('completed', completedSplit)}
      <div className="staff-queue-pending-bottom">
        <section className="staff-queue-subsection" aria-labelledby="staff-completed-med-fulfillment">
          <header className="staff-queue-subsection__head">
            <h3 id="staff-completed-med-fulfillment" className="staff-queue-subsection__title">
              Med pickup / delivery checked out (991)
            </h3>
            <span className="staff-queue-subsection__count">{completedMedFulfillmentPatients.length}</span>
          </header>
          <p className="staff-queue-subsection__lead">
            Aspen Tele / Julius checkout complete (Stage 60).
          </p>
          {renderOthersGrid(
            completedMedFulfillmentPatients,
            'completed',
            'No checked-out med pickup/delivery visits today.',
          )}
        </section>
      </div>
    </div>
  );

  const renderTelePatientCard = (p: Patient, bucket: TeleCardBucket) => {
    const medFulfillment = isMedFulfillmentVisit(p);
    const fulfillmentMode = medFulfillment ? fulfillmentModeFromRemarks(p.remarks) : null;
    const collectAt = medFulfillment ? collectAtFromRemarks(p.remarks) : null;
    const cancelled = isCancelledVisit(p.visitStage, p.remarks);
    const t = stageTint(p.visitStage, p.remarks, p.visitType);
    const open = !medFulfillment && isChatOpen(p.visitULID);
    const unreadOnQueue = medFulfillment ? 0 : patientQueueUnread(p);
    const mineChat = !medFulfillment && isMyChatPatient(p, staffId);
    const takenOther = !medFulfillment && isChatTakenByOther(p, staffId);
    const inchargeLabel = (p.chatInchargeEmpCode || '').trim() || (p.chatInchargeEmpId != null ? String(p.chatInchargeEmpId) : '');
    const canUnclaim =
      !medFulfillment &&
      bucket === 'withDoctor' &&
      (p.empId ?? 0) > 0 &&
      !isCompletedVisit(p) &&
      !isConsultFinished(p.visitStage);
    const showJuliusCheckout =
      !medFulfillment &&
      bucket === 'withDoctor' &&
      (p.empId ?? 0) > 0 &&
      !isCompletedVisit(p) &&
      isConsultFinished(p.visitStage);
    const portalHold =
      Boolean(p.consultFinalized) && Boolean(p.autoCheckoutEnabled) && p.walkIn !== true;
    const waitingFulfillment = portalHold && Boolean(p.needsFulfillmentChoice);
    const waitingExtraPay = portalHold && Boolean(p.needsExtraPayment);
    const postConsultWaiting = portalHold && (waitingFulfillment || waitingExtraPay || Boolean(p.consultFinalized));
    const atChatLimit = !open && openChats.length >= MAX_OPEN_CHATS;
    const canOpenChat = !medFulfillment && mineChat && !atChatLimit;
    const pickLoading = claimingVisitUlid === p.visitULID;
    const pickDisabled = medFulfillment || !staffId || pickLoading || takenOther || mineChat;
    const idVettingPending = !medFulfillment && p.patientIdVettingPending === true && bucket !== 'completed';
    const checkedOut = bucket === 'completed' || isCheckedOutVisit(p.visitStage, p.remarks);
    const cardBorderColor = idVettingPending ? '#f59e0b' : open ? '#34d399' : t.border;
    const cardBackground = idVettingPending ? '#fffbeb' : open ? '#ecfdf5' : t.bg;

    return (
      <div
        key={p.visitULID}
        className={`staff-tele-card patient-card patient-card--queue ${open ? 'patient-card--mine staff-tele-card--chat-open' : ''}${idVettingPending ? ' patient-card--id-vetting-pending' : ''}`}
        style={{
          borderRadius: 10,
          borderWidth: 2,
          borderStyle: 'solid',
          borderColor: cardBorderColor,
          background: cardBackground,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <div className="staff-tele-card__main" style={{ padding: '12px 14px' }}>
          <div className="patient-card__top">
            <div className="patient-card__meta">
              <div className="queue-number-wrap">
                <div className="queue-number queue-number--sm">{p.queueNo}</div>
                {!medFulfillment ? <CaseChatUnreadBadge unread={unreadOnQueue} /> : null}
              </div>
              <div className="store-badge store-badge--queue">{p.storeCode || p.store}</div>
              {medFulfillment ? (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#6b21a8',
                    background: '#f3e8ff',
                    border: '1px solid #c084fc',
                    borderRadius: 8,
                    padding: '2px 8px',
                  }}
                >
                  {fulfillmentMode ?? 'Med'} · 991
                  {collectAt ? ` · ${collectAt}` : ''}
                </span>
              ) : null}
              {!medFulfillment && p.newPt ? <NewPatientBadge size="sm" /> : null}
              {!medFulfillment && p.corporateCompanyName ? (
                <CorporateCompanyBadge name={p.corporateCompanyName} size="sm" />
              ) : null}
              {!medFulfillment && p.consultPartnerCode ? (
                <ConsultPartnerCodeBadge code={p.consultPartnerCode} size="sm" />
              ) : null}
              {!medFulfillment && p.chas?.known ? <ChasBadge chas={p.chas} size="sm" /> : null}
              {!medFulfillment && p.hssApplied ? <HssAppliedChip /> : null}
            </div>
            {!medFulfillment && (bucket === 'withDoctor' || bucket === 'completed') && (p.empId ?? 0) > 0 && (
              <span style={{ fontSize: 11, color: '#64748b' }}>Dr {p.empCode?.trim() || '—'}</span>
            )}
            {bucket === 'completed' && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: cancelled ? '#475569' : '#166534',
                  background: cancelled ? '#f1f5f9' : '#dcfce7',
                  border: cancelled ? '1px solid #94a3b8' : '1px solid #86efac',
                  borderRadius: 8,
                  padding: '2px 8px',
                }}
              >
                {cancelled ? 'Cancelled' : 'Checked out'}
              </span>
            )}
          </div>
          <div className="patient-name">{p.name}</div>
          {!medFulfillment && !cancelled && bucket !== 'completed' ? (
            <div style={{ margin: '6px 0 0' }}>
              <MeetPresenceBadge inRoom={p.patientInRoom} />
            </div>
          ) : null}
          <div className="patient-nric" style={{ opacity: 0.85 }}>
            {p.nric} · {p.age}y · {p.visitTime}
          </div>
          {!medFulfillment ? <QueueTriageSummary summary={p.triageSummary} isLightBg /> : null}
          {!medFulfillment && takenOther && inchargeLabel ? (
            <p className="staff-tele-card__incharge" style={{ margin: '10px 0 0', fontSize: 12, color: '#64748b' }}>
              Chat incharge: <strong style={{ color: '#334155' }}>{inchargeLabel}</strong>
            </p>
          ) : null}
          {!medFulfillment && mineChat ? (
            <p className="staff-tele-card__mine" style={{ margin: '8px 0 0', fontSize: 12, color: '#047857', fontWeight: 600 }}>
              You are incharge of this chat
            </p>
          ) : null}
          {medFulfillment ? (
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#6b21a8', fontWeight: 600 }}>
              {collectAt
                ? `Patient pickup ${collectAt} — pack and bill in Julius`
                : 'Med fulfillment — pack and bill in Julius (CheckLine)'}
            </p>
          ) : waitingFulfillment ? (
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#6b21a8', fontWeight: 600 }}>
              Patient is choosing pickup or delivery on the portal
            </p>
          ) : waitingExtraPay ? (
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#0f766e', fontWeight: 600 }}>
              Patient is paying the remaining balance on the portal
            </p>
          ) : null}
        </div>
        <div className="staff-tele-card__actions" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '0 12px 12px' }}>
          {!medFulfillment ? (
            <>
              <button
                type="button"
                className="staff-tele-btn staff-tele-btn--pick"
                disabled={pickDisabled}
                onClick={() => void staffPickChatForVisit(p)}
              >
                {mineChat ? 'Claimed · your chat' : pickLoading ? 'Claiming…' : 'Pick for chat'}
              </button>
              <span className="staff-tele-btn-wrap">
                <button
                  type="button"
                  className="staff-tele-btn staff-tele-btn--openchat"
                  disabled={!canOpenChat}
                  onClick={() => openOrFocusChat(p)}
                  title={
                    !mineChat
                      ? 'Pick this visit for chat first'
                      : atChatLimit
                        ? `Close a chat window first (max ${MAX_OPEN_CHATS})`
                        : unreadOnQueue > 0
                          ? `${unreadOnQueue} unread patient message${unreadOnQueue === 1 ? '' : 's'}`
                          : undefined
                  }
                >
                  {open ? 'Focus chat' : 'Open chat'}
                </button>
              </span>
            </>
          ) : null}
          <button
            type="button"
            className={`staff-tele-btn ${checkedOut || medFulfillment ? 'staff-tele-btn--view-details' : 'staff-tele-btn--dispense'}`}
            onClick={() => onSelectPatient(p)}
            title={
              medFulfillment
                ? 'View med fulfillment visit (ops in Julius)'
                : cancelled
                  ? 'View cancelled visit details (read-only)'
                  : checkedOut
                    ? 'View checked-out visit details (read-only)'
                    : 'Open patient dispense (orders, case note, MC)'
            }
          >
            {checkedOut || medFulfillment ? 'View Details' : 'Dispense'}
          </button>
          {!medFulfillment && p.newPt && bucket !== 'completed' ? (
            <PatientIdDocumentViewerButton
              visitULID={p.visitULID}
              patientName={p.name}
              patientNric={p.nric}
              vettingPending={p.patientIdVettingPending}
              onVettingComplete={() => void loadQueue()}
            />
          ) : null}
          {!medFulfillment && bucket !== 'completed' && !p.corporateZoomSessionBlocked ? (
            <CopyMeetLinkButton
              visitULID={p.visitULID}
              initialMeetUrl={meetUrlOverrides[p.visitULID] ?? p.googleMeetUrl}
              isCorporate={Boolean(p.corporateCompanyName?.trim())}
              onMeetUrlResolved={(url) =>
                setMeetUrlOverrides((prev) => ({ ...prev, [p.visitULID]: url }))
              }
            />
          ) : null}
          {!medFulfillment && bucket !== 'completed' && p.corporateZoomSessionBlocked ? (
            <span
              className="staff-tele-btn staff-tele-btn--meetlink staff-tele-btn--disabled-hint"
              title="Shared Zoom room is in use — available after the current corporate consult finishes."
            >
              Zoom link pending
            </span>
          ) : null}
        </div>
        {canUnclaim ? (
          <div style={{ padding: '0 12px 12px' }}>
            <button
              type="button"
              className="staff-queue-unclaim-btn"
              disabled={unclaimingVisitUlid === p.visitULID}
              onClick={() => void staffUnclaimVisit(p)}
            >
              {unclaimingVisitUlid === p.visitULID ? 'Unclaiming…' : 'Unclaim — return to doctor queue'}
            </button>
          </div>
        ) : showJuliusCheckout ? (
          <div style={{ padding: '0 12px 12px' }}>
            <button
              type="button"
              className="staff-queue-julius-btn"
              disabled
              title={
                waitingFulfillment
                  ? 'Doctor finished consult. Patient is choosing pickup or delivery on the portal.'
                  : waitingExtraPay
                    ? 'Doctor finished consult. Patient is paying the remaining balance on the portal.'
                    : postConsultWaiting
                      ? 'Doctor finished consult. Patient is completing extra payment or pickup/delivery on the portal.'
                      : 'Consult finished. Complete CHAS/Medisave claims and payment in Julius (CheckLine).'
              }
            >
              {waitingFulfillment
                ? 'Waiting for pickup / delivery'
                : waitingExtraPay
                  ? 'Waiting for extra payment'
                  : postConsultWaiting
                    ? 'Waiting on patient portal'
                    : 'Proceed to Julius checkout'}
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  const renderOthersGrid = (list: Patient[], bucket: TeleCardBucket, emptyMsg: string) => (
    <div className="staff-queue-duo__grid">
      {list.length === 0 ? (
        <p className="staff-queue-duo__empty">{emptyMsg}</p>
      ) : (
        list.map((p) => renderTelePatientCard(p, bucket))
      )}
    </div>
  );

  const renderStaffPendingView = () => (
    <div className="staff-queue-pending-layout">
      <div className="staff-queue-duo__col staff-queue-duo__col--mine">
        <div className="staff-queue-duo__col-head">
          <h3 className="staff-queue-duo__col-title">Your chat patients</h3>
          <span className="staff-queue-duo__col-count">{staffPendingMine.length}</span>
        </div>
        <div className="staff-queue-duo__grid">
          {staffPendingMine.length === 0 ? (
            <p className="staff-queue-duo__empty">
              None in staff pending. Pick a visit for chat from the sections below when needed.
            </p>
          ) : (
            staffPendingMine.map((p) => renderTelePatientCard(p, cardBucketForPatient(p)))
          )}
        </div>
      </div>

      <div className="staff-queue-pending-bottom">
        <section className="staff-queue-subsection" aria-labelledby="staff-pending-med-fulfillment">
          <header className="staff-queue-subsection__head">
            <h3 id="staff-pending-med-fulfillment" className="staff-queue-subsection__title">
              Med pickup / delivery (991)
            </h3>
            <span className="staff-queue-subsection__count">{medFulfillmentPendingPatients.length}</span>
          </header>
          <p className="staff-queue-subsection__lead">
            Tele med fulfillment — dispense and Aspen Tele bill in Julius (CheckLine). Purple until checked out.
          </p>
          {renderOthersGrid(
            medFulfillmentPendingPatients,
            'waiting',
            'No open med pickup/delivery visits.',
          )}
        </section>

        <section className="staff-queue-subsection" aria-labelledby="staff-pending-id-vetting">
          <header className="staff-queue-subsection__head">
            <h3 id="staff-pending-id-vetting" className="staff-queue-subsection__title">
              Verify patient ID
            </h3>
            <span className="staff-queue-subsection__count">{idVettingPatients.length}</span>
          </header>
          <p className="staff-queue-subsection__lead">
            New patients — verify portal ID before the doctor can pick the visit.
          </p>
          {renderOthersGrid(
            idVettingOthers,
            'waiting',
            'No new patients awaiting ID verification.',
          )}
        </section>

        <section className="staff-queue-subsection" aria-labelledby="staff-pending-checkout">
          <header className="staff-queue-subsection__head">
            <h3 id="staff-pending-checkout" className="staff-queue-subsection__title">
              Checkout in Julius
            </h3>
            <span className="staff-queue-subsection__count">{checkoutPendingPatients.length}</span>
          </header>
          <p className="staff-queue-subsection__lead">
            Consult finished — waiting on the patient or corporate portal for pickup/delivery, or Julius checkout.
          </p>
          {renderOthersGrid(
            checkoutPendingOthers,
            'withDoctor',
            'No visits pending Julius checkout.',
          )}
        </section>
      </div>
    </div>
  );

  const renderDoctorQueueView = () => (
    <div className="staff-queue-pending-layout">
      <div className="staff-queue-duo__col staff-queue-duo__col--mine">
        <div className="staff-queue-duo__col-head">
          <h3 className="staff-queue-duo__col-title">Your chat patients</h3>
          <span className="staff-queue-duo__col-count">{queueMine.length}</span>
        </div>
        <div className="staff-queue-duo__grid">
          {queueMine.length === 0 ? (
            <p className="staff-queue-duo__empty">
              None yet. Use <strong>Pick for chat</strong> on a visit below to claim it.
            </p>
          ) : (
            queueMine.map((p) => renderTelePatientCard(p, cardBucketForPatient(p)))
          )}
        </div>
      </div>

      <div className="staff-queue-pending-bottom">
        <section className="staff-queue-subsection" aria-labelledby="staff-queue-no-doctor">
          <header className="staff-queue-subsection__head">
            <h3 id="staff-queue-no-doctor" className="staff-queue-subsection__title">
              No doctor yet
            </h3>
            <span className="staff-queue-subsection__count">{queueWaitingPatients.length}</span>
          </header>
          <p className="staff-queue-subsection__lead">
            Tele visits waiting for a doctor to pick and start the consult.
          </p>
          {renderOthersGrid(
            queueWaitingOthers,
            'waiting',
            'No visits waiting for a doctor.',
          )}
        </section>

        <section className="staff-queue-subsection" aria-labelledby="staff-queue-with-doctor">
          <header className="staff-queue-subsection__head">
            <h3 id="staff-queue-with-doctor" className="staff-queue-subsection__title">
              With doctor — in consult
            </h3>
            <span className="staff-queue-subsection__count">{queueWithDoctorPatients.length}</span>
          </header>
          <p className="staff-queue-subsection__lead">
            Doctor picked; consult in progress (before dispense is saved).
          </p>
          {renderOthersGrid(
            queueWithDoctorOthers,
            'withDoctor',
            'No other visits currently in consult.',
          )}
        </section>
      </div>
    </div>
  );

  const renderDuoVertical = (bucket: TeleCardBucket, split: { mine: Patient[]; others: Patient[] }) => (
    <div className="staff-queue-duo staff-queue-duo--stacked">
      <div className="staff-queue-duo__col staff-queue-duo__col--mine">
        <div className="staff-queue-duo__col-head">
          <h3 className="staff-queue-duo__col-title">Chat patients</h3>
          <span className="staff-queue-duo__col-count">{split.mine.length}</span>
        </div>
        <div className="staff-queue-duo__grid">
          {split.mine.length === 0 ? (
            <p className="staff-queue-duo__empty">
              None yet. Use <strong>Pick for chat</strong> on a visit below to claim it.
            </p>
          ) : (
            split.mine.map((p) => renderTelePatientCard(p, bucket))
          )}
        </div>
      </div>
      <div className="staff-queue-duo__col staff-queue-duo__col--others">
        <div className="staff-queue-duo__col-head">
          <h3 className="staff-queue-duo__col-title">All other visits</h3>
          <span className="staff-queue-duo__col-count">{split.others.length}</span>
        </div>
        <div className="staff-queue-duo__grid">
          {split.others.length === 0 ? (
            <p className="staff-queue-duo__empty">No other visits in this queue.</p>
          ) : (
            split.others.map((p) => renderTelePatientCard(p, bucket))
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className={`app-container staff-teleconsult-app${queueTab === 'management' ? ' app-container--mgmt' : ''}`}>
      <button type="button" className="mobile-menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
        {sidebarOpen ? '✕' : '☰'}
      </button>
      <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />

      <aside className={`sidebar staff-tele-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo sidebar-logo--brand">
          <AspenHealthLogo variant="sidebar" showPortalLabel />
        </div>
        <ul className="sidebar-menu">
          <li>
            <a
              href="#queue"
              className={queueTab !== 'management' ? 'active' : ''}
              onClick={(e) => {
                e.preventDefault();
                if (queueTab === 'management') {
                  setQueueTab('queue');
                }
                setSidebarOpen(false);
              }}
            >
              Queue &amp; chat
            </a>
          </li>
          {showManagementTab ? (
            <li>
              <a
                href="#management"
                className={queueTab === 'management' ? 'active' : ''}
                onClick={(e) => {
                  e.preventDefault();
                  setQueueTab('management');
                  setSidebarOpen(false);
                }}
              >
                Management
              </a>
            </li>
          ) : null}
        </ul>
        <div className="doctor-info">
          <div className="doctor-name">{staff?.name}</div>
          <div className="doctor-mcr">
            Tele visit and support chats stay open at the bottom when you switch tabs.
          </div>
          <button type="button" className="btn btn-secondary staff-tele-logout" onClick={onLogout}>
            Log out
          </button>
        </div>
      </aside>

      <main className="main-content staff-teleconsult-main">
        {queueTab === 'management' ? null : (
        <div className="page-header staff-tele-page-header">
          <h1 className="page-title">
            {queueTab === 'support'
              ? 'Support'
              : queueTab === 'completed'
                ? 'Completed tele visits'
                : queueTab === 'pending'
                  ? 'Staff pending'
                  : 'Visit Queue'}
          </h1>
          {queueTab === 'queue' ? (
            <p className="staff-tele-intro">
              Visits waiting for a doctor to consult. <strong>Pick for chat</strong> assigns the patient conversation to you;{' '}
              <strong>Open chat</strong> is available only after you have picked.
            </p>
          ) : queueTab === 'pending' ? (
            <p className="staff-tele-intro">
              Staff action required — verify new patient ID, or complete Julius checkout after the doctor finishes the consult.
            </p>
          ) : null}
        </div>
        )}

        {queueTab === 'management' ? null : (
        <div className="staff-queue-tabs" role="tablist" aria-label="Staff queue">
          <button
            type="button"
            role="tab"
            aria-selected={queueTab === 'pending'}
            className={`staff-queue-tabs__btn ${queueTab === 'pending' ? 'staff-queue-tabs__btn--active' : ''}${staffPendingUnreadTotal > 0 ? ' staff-queue-tabs__btn--has-unread' : ''}`}
            onClick={() => setQueueTab('pending')}
          >
            <span className="staff-queue-tabs__label">Pending({staffPendingPatients.length})</span>
            <QueueTabChatIcon unread={staffPendingUnreadTotal} />
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={queueTab === 'queue'}
            className={`staff-queue-tabs__btn ${queueTab === 'queue' ? 'staff-queue-tabs__btn--active' : ''}${doctorQueueUnreadTotal > 0 ? ' staff-queue-tabs__btn--has-unread' : ''}`}
            onClick={() => setQueueTab('queue')}
          >
            <span className="staff-queue-tabs__label">Visit Queue({doctorQueuePatients.length})</span>
            <QueueTabChatIcon unread={doctorQueueUnreadTotal} />
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={queueTab === 'support'}
            className={`staff-queue-tabs__btn ${queueTab === 'support' ? 'staff-queue-tabs__btn--active' : ''}${supportUnhandledCount > 0 ? ' staff-queue-tabs__btn--has-unread' : ''}`}
            onClick={() => setQueueTab('support')}
          >
            <span className="staff-queue-tabs__label">Support({supportActiveCount})</span>
            <QueueTabChatIcon unread={supportUnhandledCount} />
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={queueTab === 'completed'}
            className={`staff-queue-tabs__btn ${queueTab === 'completed' ? 'staff-queue-tabs__btn--active' : ''}${completedUnreadTotal > 0 ? ' staff-queue-tabs__btn--has-unread' : ''}`}
            onClick={() => setQueueTab('completed')}
          >
            <span className="staff-queue-tabs__label">Completed({completedPatients.length})</span>
            <QueueTabChatIcon unread={completedUnreadTotal} />
          </button>
        </div>
        )}

        {queueTab === 'support' ? (
          <section className="staff-queue-section staff-queue-section--tabbed">
            <StaffNormalSupportQueue />
          </section>
        ) : queueTab === 'management' ? (
          <section className="staff-queue-section staff-queue-section--tabbed">
            <StaffManagementPanel />
          </section>
        ) : (
          <>
            <div className="filter-bar">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                aria-label="Visit date"
              />
              {isTeleconsultQueueStoreLocked() ? (
                <span className="filter-bar__store-label" aria-label="Clinic">
                  {formatTeleconsultQueueStoreLabel(stores)}
                </span>
              ) : (
                <select value={storeFilter} onChange={(e) => setStoreFilter(e.target.value)}>
                  <option value="">All Stores</option>
                  {stores.map((store) => (
                    <option key={store.id} value={store.id.toString()}>
                      {store.code} - {store.storeName}
                    </option>
                  ))}
                </select>
              )}
              <input
                type="search"
                placeholder="Search name or NRIC…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search queue"
              />
            </div>

            {error && <div className="staff-tele-alert staff-tele-alert--error">{error}</div>}

            {openChats.length >= MAX_OPEN_CHATS && (
              <p className="staff-tele-hint staff-tele-hint--warning">
                Maximum {MAX_OPEN_CHATS} chats open — close one to open another.
              </p>
            )}

            {loading ? (
              <div className="staff-tele-loading">Loading tele visits…</div>
            ) : (
              <section className="staff-queue-section staff-queue-section--tabbed">
                <header className="staff-queue-section__header staff-queue-section__header--tabbed">
                  <p className="staff-queue-section__lead">
                    {queueTab === 'pending'
                      ? 'New patient ID verification and post-consult Julius checkout.'
                      : queueTab === 'queue'
                        ? 'Waiting for doctor consult — your chat patients first, then no-doctor and in-consult lists.'
                        : 'Checked-out visits (Stage 60) after Julius / Aspen Tele payment — tele chats above, med 991 below.'}
                  </p>
                  <span className="staff-queue-section__count">
                    {queueTab === 'pending'
                      ? staffPendingPatients.length
                      : queueTab === 'queue'
                        ? doctorQueuePatients.length
                        : completedPatients.length}{' '}
                    visits
                  </span>
                </header>
                {queueTab === 'pending'
                  ? renderStaffPendingView()
                  : queueTab === 'queue'
                    ? renderDoctorQueueView()
                    : renderCompletedView()}
              </section>
            )}
          </>
        )}
      </main>

      <StaffMessengerHubProvider threads={messengerThreads} onSwitch={switchMessengerThread}>
      <div className="staff-messenger-docks" aria-label="Open conversations">
      {openChats.length > 0 ? (
      <div className="staff-messenger-dock staff-messenger-dock--tele" aria-label="Tele visit conversations">
        {openChats.map((c, stackIndex) => {
          const unread = unreadPatientCount(c);
          const qPatient = patients.find((x) => x.visitULID === c.visitUlid);
          const showRelease =
            !!staffId &&
            (qPatient ? isMyChatPatient(qPatient, staffId) : isMyChatPatient(c.patient, staffId));
          return (
            <div
              key={c.visitUlid}
              className={`staff-messenger-slot ${c.expanded ? 'staff-messenger-slot--expanded' : ''}`}
              style={{ zIndex: 100 + stackIndex }}
            >
              {c.expanded ? (
                <div className="staff-messenger-window">
                  <div className="staff-messenger-window__titlebar">
                    <div className="staff-messenger-window__title">
                      <strong>{c.patient.name}</strong>
                      <span className="staff-messenger-window__sub">{c.patient.queueNo}</span>
                    </div>
                    {showRelease ? (
                      <div className="staff-messenger-window__actions">
                        <button
                          type="button"
                          className="staff-messenger-textbtn"
                          title="Release chat claim so another staff can pick this visit"
                          disabled={releasingChatVisitUlid === c.visitUlid || c.isClosed}
                          onClick={() => void staffReleaseChatClaim(c.visitUlid)}
                        >
                          {releasingChatVisitUlid === c.visitUlid ? 'Releasing…' : 'Release'}
                        </button>
                        <button
                          type="button"
                          className="staff-messenger-textbtn staff-messenger-textbtn--end"
                          title="End chat for this visit"
                          disabled={closingChatVisitUlid === c.visitUlid || c.isClosed}
                          onClick={() => void staffCloseChat(c.visitUlid)}
                        >
                          {closingChatVisitUlid === c.visitUlid ? 'Ending…' : 'End chat'}
                        </button>
                      </div>
                    ) : null}
                    <div className="staff-messenger-window__chrome">
                      <button
                        type="button"
                        className="staff-messenger-iconbtn"
                        title="Minimize"
                        onClick={() => setExpanded(c.visitUlid, false)}
                      >
                        ─
                      </button>
                      <button
                        type="button"
                        className="staff-messenger-iconbtn"
                        title="Close"
                        onClick={() => closeChat(c.visitUlid)}
                      >
                        ×
                      </button>
                    </div>
                    <StaffMessengerThreadSwitcher />
                  </div>
                  {c.chatError && (
                    <div className="staff-messenger-window__banner">{c.chatError}</div>
                  )}
                  {c.isClosed ? (
                    <div className="staff-messenger-window__banner staff-messenger-window__banner--info">
                      This chat has ended. Pick the visit again to open a new conversation.
                    </div>
                  ) : null}
                  <StaffChatMessagesPanel
                    visitUlid={c.visitUlid}
                    messages={c.messages}
                    onPatientIdSaved={() => void loadQueue({ silent: true })}
                  />
                  <form
                    className="staff-messenger-window__form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void sendFor(c.visitUlid);
                    }}
                  >
                    <div className="staff-messenger-compose">
                      <textarea
                        className="staff-messenger-compose__field"
                        value={c.draft}
                        onChange={(e) => setDraftFor(c.visitUlid, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            void sendFor(c.visitUlid);
                          }
                        }}
                        placeholder={c.isClosed ? 'Chat ended' : 'Message the patient…'}
                        rows={3}
                        disabled={c.isClosed}
                        aria-busy={c.sending}
                        autoComplete="off"
                      />
                      <div className="staff-messenger-compose__actions">
                        {showRelease ? (
                          <button
                            type="button"
                            className="staff-messenger-compose__money"
                            disabled={c.sending}
                            aria-label="Send Stripe payment link"
                            title="Payment link (Stripe)"
                            onClick={() => {
                              setStripePaymentVisitUlid(c.visitUlid);
                              setStripePaymentAmount('');
                              setStripePaymentError(null);
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              aria-hidden
                            >
                              <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" />
                            </svg>
                          </button>
                        ) : null}
                        <button
                          type="submit"
                          className="staff-messenger-compose__send"
                          disabled={c.isClosed || c.sending || !c.draft.trim()}
                          aria-label={c.sending ? 'Sending…' : 'Send message'}
                          title="Send message"
                        >
                          {c.sending ? (
                            <span className="staff-messenger-compose__sending" aria-hidden />
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              aria-hidden
                            >
                              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                    <p className="staff-messenger-compose__hint">
                      {c.isClosed ? 'Chat ended' : 'Enter to send · Shift+Enter for a new line'}
                    </p>
                  </form>
                  {stripePaymentVisitUlid === c.visitUlid ? (
                    <div
                      className="staff-messenger-stripe-dialog-backdrop"
                      role="presentation"
                      onClick={() => {
                        if (!c.sending) closeStripePaymentDialog();
                      }}
                    >
                      <div
                        className="staff-messenger-stripe-dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={`stripe-pay-title-${c.visitUlid}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <h2 className="staff-messenger-stripe-dialog__title" id={`stripe-pay-title-${c.visitUlid}`}>
                          Payment link
                        </h2>
                        <p className="staff-messenger-stripe-dialog__lead">
                          Amount in SGD. A secure Stripe checkout link will be posted to this chat.
                        </p>
                        <label className="staff-messenger-stripe-dialog__label" htmlFor={`stripe-amt-${c.visitUlid}`}>
                          Amount (SGD)
                        </label>
                        <input
                          id={`stripe-amt-${c.visitUlid}`}
                          className="staff-messenger-stripe-dialog__input"
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          placeholder="e.g. 35.00"
                          value={stripePaymentAmount}
                          disabled={c.sending}
                          onChange={(e) => setStripePaymentAmount(e.target.value)}
                        />
                        {stripePaymentError ? (
                          <p className="staff-messenger-stripe-dialog__error">{stripePaymentError}</p>
                        ) : null}
                        <div className="staff-messenger-stripe-dialog__buttons">
                          <button
                            type="button"
                            className="staff-messenger-stripe-dialog__secondary"
                            disabled={c.sending}
                            onClick={() => closeStripePaymentDialog()}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="staff-messenger-stripe-dialog__primary"
                            disabled={c.sending}
                            onClick={() => {
                              const n = parseSgdStaffPaymentAmount(stripePaymentAmount);
                              if (n == null) {
                                setStripePaymentError('Enter an amount between 0.01 and 50,000 SGD.');
                                return;
                              }
                              void sendPaymentLinkInChat(c.visitUlid, n);
                            }}
                          >
                            {c.sending ? 'Working…' : 'Create link & send'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <button
                  type="button"
                  className="staff-messenger-pill"
                  onClick={() => setExpanded(c.visitUlid, true)}
                  title={`Open chat: ${c.patient.name}`}
                  aria-label={
                    unread > 0
                      ? `Open chat with ${c.patient.name}, ${unread} unread`
                      : `Open chat with ${c.patient.name}`
                  }
                >
                  <span className="staff-messenger-pill__avatar" aria-hidden>
                    {chatHeadInitials(c.patient.name)}
                  </span>
                  <span className="staff-messenger-pill__name">{c.patient.name}</span>
                  {unread > 0 && <span className="staff-messenger-pill__badge">{unread > 9 ? '9+' : unread}</span>}
                </button>
              )}
            </div>
          );
        })}
      </div>
      ) : null}
      <StaffNormalSupportDock />
      {patientReadyToasts.length > 0 ? (
        <div className="staff-patient-ready-toasts" aria-live="polite">
          {patientReadyToasts.map((t) => (
            <div key={t.key} className="staff-patient-ready-toast">
              <p className="staff-patient-ready-toast__text">
                <strong>{t.patientName}</strong> is ready in the video call
              </p>
              <button
                type="button"
                className="staff-patient-ready-toast__dismiss"
                onClick={() => dismissPatientReadyToast(t.key)}
                aria-label="Dismiss notification"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : null}
      </div>
      </StaffMessengerHubProvider>
    </div>
  );
}
